import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Plus,
  Trash2,
  Edit2,
  Eye,
  ChevronLeft,
  ChevronRight,
  Search,
  Upload,
  X,
  Check,
  AlertCircle,
  RefreshCw,
  ArchiveRestore,
  ToggleLeft,
  ToggleRight,
  AlertTriangle,
  Loader,
} from "lucide-react";
import { useTheme } from "../context/ThemeContext";

const API_BASE_URL =
  process.env.REACT_APP_BASE_URL || "http://localhost:3000/api/v1";

const StickersManagement = () => {
  const { isDarkMode } = useTheme(); // ✅ now using theme context

  // ==================== STATE ====================
  const [stickers, setStickers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [currentPage, setCurrentPage] = useState(() => {
    const saved = sessionStorage.getItem("stickersPage");
    return saved ? parseInt(saved, 10) : 1;
  });
  const [pagination, setPagination] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [deletedFilter, setDeletedFilter] = useState("false");

  // Modals and forms
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedSticker, setSelectedSticker] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formData, setFormData] = useState({ name: "", imageUrl: "" });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  // ==================== THEME ====================
  const theme = {
    bg: isDarkMode ? "bg-black" : "bg-gray-50",
    cardBg: isDarkMode ? "bg-black" : "bg-white",
    textPrimary: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    border: isDarkMode ? "border-zinc-800" : "border-gray-200",
    hover: isDarkMode ? "hover:bg-zinc-900" : "hover:bg-gray-50",
    inputBg: isDarkMode ? "bg-zinc-900" : "bg-gray-100",
    inputBorder: isDarkMode ? "border-zinc-700" : "border-gray-300",
  };

  // ==================== CONFIRMATION MODAL COMPONENT ====================
  const ConfirmModal = ({
    isOpen,
    onClose,
    onConfirm,
    title,
    message,
    confirmText = "Delete",
    isDarkMode,
    isLoading = false,
  }) => {
    if (!isOpen) return null;

    const theme = {
      cardBg: isDarkMode ? "bg-black" : "bg-white",
      textPrimary: isDarkMode ? "text-white" : "text-gray-900",
      textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
      border: isDarkMode ? "border-zinc-800" : "border-gray-200",
      hover: isDarkMode ? "hover:bg-zinc-900" : "hover:bg-gray-50",
    };

    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in">
        <div
          className={`${theme.cardBg} rounded-2xl max-w-md w-full p-6 border ${theme.border} animate-in zoom-in-95`}
        >
          <div className="flex items-start gap-4 mb-6">
            <div className="p-3 rounded-full bg-red-500/10">
              <AlertTriangle className="w-6 h-6 text-red-500" />
            </div>
            <div className="flex-1">
              <h2 className={`text-xl font-bold ${theme.textPrimary} mb-2`}>
                {title}
              </h2>
              <p className={`text-sm ${theme.textSecondary}`}>{message}</p>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className={`flex-1 px-4 py-2.5 rounded-lg font-medium border ${theme.border} ${theme.textPrimary} ${theme.hover} transition-colors disabled:opacity-50`}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={isLoading}
              className="flex-1 px-4 py-2.5 bg-red-500 text-white font-semibold rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <RefreshCw size={16} className="animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Trash2 size={16} />
                  {confirmText}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ==================== AXIOS CONFIG ====================
  const getAxiosConfig = () => {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      window.location.href = "/login";
      return null;
    }
    return {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    };
  };

  // ==================== SESSION STORAGE ====================
  useEffect(() => {
    sessionStorage.setItem("stickersPage", currentPage.toString());
  }, [currentPage]);

  // ==================== FETCH STICKERS WITH RETRY ====================
  const fetchStickers = useCallback(async () => {
    const maxRetries = 5;
    const retryDelay = 10000; // 10 seconds
    let retryCount = 0;

    const attemptFetch = async () => {
      try {
        setLoading(true);
        const config = getAxiosConfig();
        if (!config) return;

        const params = new URLSearchParams({
          page: currentPage.toString(),
          limit: "10",
        });

        if (statusFilter !== "all") params.append("status", statusFilter);
        if (deletedFilter !== "all") params.append("deleted", deletedFilter);
        if (searchTerm) params.append("search", searchTerm);

        const response = await axios.get(
          `${API_BASE_URL}/stickers/admin/all?${params.toString()}`,
          config
        );

        setStickers(response.data.data || []);
        setPagination(response.data);
        setError(null);
      } catch (err) {
        console.error(
          `Error fetching stickers (attempt ${retryCount + 1}):`,
          err
        );

        if (err.response?.status === 401) {
          localStorage.removeItem("accessToken");
          window.location.href = "/login";
          return;
        }

        // Retry logic
        if (retryCount < maxRetries - 1) {
          retryCount++;
          setError(
            `Failed to fetch stickers. Retrying in 10 seconds... (${retryCount}/${maxRetries})`
          );
          await new Promise((resolve) => setTimeout(resolve, retryDelay));
          return attemptFetch();
        } else {
          setError(
            err.response?.data?.message ||
              "Failed to fetch stickers after multiple attempts"
          );
        }
      } finally {
        setLoading(false);
      }
    };

    await attemptFetch();
  }, [currentPage, searchTerm, statusFilter, deletedFilter]);

  useEffect(() => {
    fetchStickers();
  }, [fetchStickers]);

  // ==================== IMAGE HANDLING ====================
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
      if (!validTypes.includes(file.type)) {
        setError("Please select a valid image file (JPEG, PNG, GIF, WebP)");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError("Image size must be less than 5MB");
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
      setError(null);
    }
  };

  // ==================== S3 UPLOAD (FIXED) ====================
  const uploadImage = async () => {
    if (!imageFile) return null;

    try {
      setUploading(true);
      const config = getAxiosConfig();
      if (!config) return null;

      // ✅ STEP 1: Get presigned URL with proper request body
      const presignedResponse = await axios.post(
        `${API_BASE_URL}/uploads/presigned-url`,
        {
          fileName: imageFile.name,
          type: "support", // or whatever type you need
        },
        config
      );

      const { uploadUrl, key } = presignedResponse.data.data;

      // ✅ STEP 2: Convert file to blob
      const blob = new Blob([imageFile], { type: imageFile.type });

      // ✅ STEP 3: Upload to S3 using PUT
      const uploadResponse = await fetch(uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": imageFile.type,
        },
        body: blob,
      });

      if (!uploadResponse.ok) {
        throw new Error("Failed to upload to S3");
      }

      // ✅ STEP 4: Return final URL
      const finalUrl = uploadUrl.split("?")[0];
      return finalUrl;
    } catch (err) {
      console.error("Error uploading image:", err);
      setError(err.response?.data?.message || "Failed to upload image");
      return null;
    } finally {
      setUploading(false);
    }
  };

  // ==================== CREATE STICKER ====================
  const handleCreateSticker = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError("Please enter a sticker name");
      return;
    }
    if (!imageFile && !formData.imageUrl) {
      setError("Please select an image or provide image URL");
      return;
    }

    try {
      setFormSubmitting(true);
      setError(null);
      const config = getAxiosConfig();
      if (!config) return;

      let imageUrl = formData.imageUrl;

      if (imageFile) {
        imageUrl = await uploadImage();
        if (!imageUrl) return;
      }

      await axios.post(
        `${API_BASE_URL}/stickers/admin`,
        { name: formData.name.trim(), imageUrl },
        config
      );

      setSuccess("Sticker created successfully!");
      setShowCreateModal(false);
      setFormData({ name: "", imageUrl: "" });
      setImageFile(null);
      setImagePreview(null);
      setCurrentPage(1);
      fetchStickers();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to create sticker");
    } finally {
      setFormSubmitting(false);
    }
  };

  // ==================== UPDATE STICKER ====================
  const handleUpdateSticker = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError("Please enter a sticker name");
      return;
    }

    try {
      setFormSubmitting(true);
      setError(null);
      const config = getAxiosConfig();
      if (!config) return;

      await axios.patch(
        `${API_BASE_URL}/stickers/admin/${selectedSticker._id}`,
        { name: formData.name.trim() },
        config
      );

      setSuccess("Sticker updated successfully!");
      setShowEditModal(false);
      setFormData({ name: "", imageUrl: "" });
      fetchStickers();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update sticker");
    } finally {
      setFormSubmitting(false);
    }
  };

  // ==================== DELETE STICKER ====================
  const handleDeleteSticker = async (id) => {
    setConfirmAction(() => async () => {
      try {
        setFormSubmitting(true);
        const config = getAxiosConfig();
        if (!config) return;

        await axios.delete(`${API_BASE_URL}/stickers/admin/${id}`, config);
        setSuccess("Sticker deleted successfully!");
        setShowConfirmModal(false);
        fetchStickers();
      } catch (err) {
        setError(err.response?.data?.message || "Failed to delete sticker");
      } finally {
        setFormSubmitting(false);
      }
    });
    setShowConfirmModal(true);
  };

  // ==================== RESTORE STICKER ====================
  const handleRestoreSticker = async (id) => {
    try {
      const config = getAxiosConfig();
      if (!config) return;

      await axios.patch(
        `${API_BASE_URL}/stickers/admin/${id}/restore`,
        {},
        config
      );
      setSuccess("Sticker restored successfully!");
      fetchStickers();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to restore sticker");
    }
  };

  // ==================== TOGGLE STATUS ====================
  const handleToggleStatus = async (id, currentStatus) => {
    try {
      const config = getAxiosConfig();
      if (!config) return;

      const endpoint = currentStatus ? "deactivate" : "activate";
      await axios.patch(
        `${API_BASE_URL}/stickers/admin/${id}/${endpoint}`,
        {},
        config
      );
      setSuccess(
        `Sticker ${currentStatus ? "deactivated" : "activated"} successfully!`
      );
      fetchStickers();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update status");
    }
  };

  // ==================== MODAL HANDLERS ====================
  const openCreateModal = () => {
    setFormData({ name: "", imageUrl: "" });
    setImageFile(null);
    setImagePreview(null);
    setError(null);
    setShowCreateModal(true);
  };

  const openEditModal = (sticker) => {
    setSelectedSticker(sticker);
    setFormData({ name: sticker.name, imageUrl: sticker.imageUrl });
    setImageFile(null);
    setImagePreview(null);
    setError(null);
    setShowEditModal(true);
  };

  const openViewModal = (sticker) => {
    setSelectedSticker(sticker);
    setShowViewModal(true);
  };

  const closeModals = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setShowViewModal(false);
    setSelectedSticker(null);
    setFormData({ name: "", imageUrl: "" });
    setImageFile(null);
    setImagePreview(null);
    setError(null);
  };

  // ==================== MESSAGE TIMERS ====================
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // ==================== LOADING STATE ====================
  if (loading && stickers.length === 0) {
    return (
      <div className={`flex flex-col min-h-screen ${theme.bg} p-6`}>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <Loader className="animate-spin w-12 h-12 mx-auto mb-4 text-orange-400" />
            <p className={`text-xl ${theme.textPrimary}`}>
              Loading stickers...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ==================== RENDER ====================
  return (
    <div className={`min-h-screen ${theme.bg}`}>
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className={`text-3xl font-bold ${theme.textPrimary}`}>
              Stickers Management
            </h1>
            <p className={`${theme.textSecondary} mt-1`}>
              Manage stickers for user reactions and interactions
            </p>
          </div>
          <button
            onClick={openCreateModal}
            className="flex items-center gap-2 px-6 py-3 bg-orange-400 text-black font-semibold rounded-lg hover:bg-orange-500 transition-colors shadow-lg"
          >
            <Plus size={20} />
            Add Sticker
          </button>
        </div>

        {/* ========== MESSAGES ========== */}
        {error && (
          <div
            className={`border rounded-xl p-4 flex items-start gap-3 animate-in fade-in ${
              isDarkMode
                ? "bg-red-500/10 border-red-500/30 text-red-400"
                : "bg-red-50 border-red-200 text-red-700"
            }`}
          >
            <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
            <div className="flex-1">{error}</div>
            <button
              onClick={() => setError(null)}
              className="flex-shrink-0 hover:opacity-70"
            >
              <X size={18} />
            </button>
          </div>
        )}

        {success && (
          <div
            className={`border rounded-xl p-4 flex items-start gap-3 animate-in fade-in ${
              isDarkMode
                ? "bg-green-500/10 border-green-500/30 text-green-400"
                : "bg-green-50 border-green-200 text-green-700"
            }`}
          >
            <Check size={20} className="flex-shrink-0 mt-0.5" />
            <div className="flex-1">{success}</div>
            <button
              onClick={() => setSuccess(null)}
              className="flex-shrink-0 hover:opacity-70"
            >
              <X size={18} />
            </button>
          </div>
        )}

        {/* ========== FILTERS ========== */}
        <div
          className={`${theme.cardBg} border ${theme.border} rounded-xl p-5`}
        >
          <div className="space-y-5">
            {/* Search */}
            <div>
              <label
                className={`block text-sm font-medium ${theme.textSecondary} mb-2`}
              >
                Search Stickers
              </label>
              <div className="relative">
                <Search
                  size={18}
                  className={`absolute left-3 top-3 ${theme.textSecondary}`}
                />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  placeholder="Search by sticker name..."
                  className={`w-full pl-10 pr-4 py-2 rounded-lg border ${theme.inputBorder} ${theme.inputBg} ${theme.textPrimary} focus:outline-none focus:ring-2 focus:ring-orange-400 transition-all`}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Status Filter */}
              <div>
                <label
                  className={`block text-sm font-medium ${theme.textSecondary} mb-3`}
                >
                  Status
                </label>
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={() => {
                      setStatusFilter("all");
                      setCurrentPage(1);
                    }}
                    className={`px-4 py-2 rounded-lg font-medium transition-all ${
                      statusFilter === "all"
                        ? "bg-orange-400 text-black shadow-lg"
                        : isDarkMode
                        ? "bg-zinc-900 text-gray-400 hover:bg-zinc-800 border border-zinc-700"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300"
                    }`}
                  >
                    All
                  </button>
                  <button
                    onClick={() => {
                      setStatusFilter("active");
                      setCurrentPage(1);
                    }}
                    className={`px-4 py-2 rounded-lg font-medium transition-all ${
                      statusFilter === "active"
                        ? "bg-orange-400 text-black shadow-lg"
                        : isDarkMode
                        ? "bg-zinc-900 text-gray-400 hover:bg-zinc-800 border border-zinc-700"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300"
                    }`}
                  >
                    Active
                  </button>
                  <button
                    onClick={() => {
                      setStatusFilter("inactive");
                      setCurrentPage(1);
                    }}
                    className={`px-4 py-2 rounded-lg font-medium transition-all ${
                      statusFilter === "inactive"
                        ? "bg-orange-400 text-black shadow-lg"
                        : isDarkMode
                        ? "bg-zinc-900 text-gray-400 hover:bg-zinc-800 border border-zinc-700"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300"
                    }`}
                  >
                    Inactive
                  </button>
                </div>
              </div>

              {/* Deleted Filter */}
              <div>
                <label
                  className={`block text-sm font-medium ${theme.textSecondary} mb-3`}
                >
                  Deleted
                </label>
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={() => {
                      setDeletedFilter("false");
                      setCurrentPage(1);
                    }}
                    className={`px-4 py-2 rounded-lg font-medium transition-all ${
                      deletedFilter === "false"
                        ? "bg-orange-400 text-black shadow-lg"
                        : isDarkMode
                        ? "bg-zinc-900 text-gray-400 hover:bg-zinc-800 border border-zinc-700"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300"
                    }`}
                  >
                    Active Only
                  </button>
                  <button
                    onClick={() => {
                      setDeletedFilter("true");
                      setCurrentPage(1);
                    }}
                    className={`px-4 py-2 rounded-lg font-medium transition-all ${
                      deletedFilter === "true"
                        ? "bg-orange-400 text-black shadow-lg"
                        : isDarkMode
                        ? "bg-zinc-900 text-gray-400 hover:bg-zinc-800 border border-zinc-700"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300"
                    }`}
                  >
                    Deleted Only
                  </button>
                  <button
                    onClick={() => {
                      setDeletedFilter("all");
                      setCurrentPage(1);
                    }}
                    className={`px-4 py-2 rounded-lg font-medium transition-all ${
                      deletedFilter === "all"
                        ? "bg-orange-400 text-black shadow-lg"
                        : isDarkMode
                        ? "bg-zinc-900 text-gray-400 hover:bg-zinc-800 border border-zinc-700"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300"
                    }`}
                  >
                    All
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ========== TABLE ========== */}
        <div
          className={`${theme.cardBg} border ${theme.border} rounded-xl overflow-hidden`}
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className={theme.inputBg}>
                <tr>
                  <th
                    className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider ${theme.textSecondary}`}
                  >
                    Image
                  </th>
                  <th
                    className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider ${theme.textSecondary}`}
                  >
                    Name
                  </th>
                  <th
                    className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider ${theme.textSecondary}`}
                  >
                    Status
                  </th>
                  <th
                    className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider ${theme.textSecondary}`}
                  >
                    Created
                  </th>
                  <th
                    className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider ${theme.textSecondary}`}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className={`divide-y ${theme.border}`}>
                {stickers.length === 0 ? (
                  <tr>
                    <td
                      colSpan="5"
                      className={`px-6 py-12 text-center ${theme.textSecondary}`}
                    >
                      <div className="flex flex-col items-center gap-2">
                        <AlertCircle size={32} className="opacity-50" />
                        <p>No stickers found</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  stickers.map((sticker) => (
                    <tr
                      key={sticker._id}
                      className={`border-t ${theme.border} ${theme.hover} transition-colors`}
                    >
                      <td className="px-6 py-4">
                        <img
                          src={sticker.imageUrl}
                          alt={sticker.name}
                          className="w-10 h-10 rounded object-cover"
                          onError={(e) => {
                            e.target.src =
                              "https://via.placeholder.com/40?text=No";
                          }}
                        />
                      </td>
                      <td
                        className={`px-6 py-4 font-medium ${theme.textPrimary}`}
                      >
                        {sticker.name}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          {sticker.isDeleted ? (
                            <span
                              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
                                isDarkMode
                                  ? "bg-red-500/20 text-red-400 border border-red-500/30"
                                  : "bg-red-100 text-red-700 border border-red-300"
                              }`}
                            >
                              Deleted
                            </span>
                          ) : (
                            <span
                              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
                                sticker.isActive
                                  ? isDarkMode
                                    ? "bg-green-500/20 text-green-400 border border-green-500/30"
                                    : "bg-green-100 text-green-700 border border-green-300"
                                  : isDarkMode
                                  ? "bg-gray-500/20 text-gray-400 border border-gray-500/30"
                                  : "bg-gray-100 text-gray-700 border border-gray-300"
                              }`}
                            >
                              {sticker.isActive ? "Active" : "Inactive"}
                            </span>
                          )}
                        </div>
                      </td>
                      <td
                        className={`px-6 py-4 text-sm ${theme.textSecondary}`}
                      >
                        {new Date(sticker.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openViewModal(sticker)}
                            className={`p-2 rounded transition-colors ${theme.hover}`}
                            title="View"
                          >
                            <Eye size={20} className={theme.textSecondary} />
                          </button>

                          {!sticker.isDeleted && (
                            <>
                              <button
                                onClick={() => openEditModal(sticker)}
                                className={`p-2 rounded transition-colors ${theme.hover}`}
                                title="Edit"
                              >
                                <Edit2
                                  size={20}
                                  className={theme.textSecondary}
                                />
                              </button>

                              <button
                                onClick={() =>
                                  handleToggleStatus(
                                    sticker._id,
                                    sticker.isActive
                                  )
                                }
                                className={`p-2 rounded transition-colors ${theme.hover}`}
                                title={
                                  sticker.isActive ? "Deactivate" : "Activate"
                                }
                              >
                                {sticker.isActive ? (
                                  <ToggleRight
                                    size={20}
                                    className="text-green-400"
                                  />
                                ) : (
                                  <ToggleLeft
                                    size={20}
                                    className={theme.textSecondary}
                                  />
                                )}
                              </button>

                              <button
                                onClick={() => handleDeleteSticker(sticker._id)}
                                className={`p-2 rounded transition-colors ${theme.hover}`}
                                title="Delete"
                              >
                                <Trash2 size={20} className="text-red-400" />
                              </button>
                            </>
                          )}

                          {sticker.isDeleted && (
                            <button
                              onClick={() => handleRestoreSticker(sticker._id)}
                              className={`p-2 rounded transition-colors ${theme.hover}`}
                              title="Restore"
                            >
                              <ArchiveRestore
                                size={20}
                                className="text-blue-400"
                              />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* ========== PAGINATION ========== */}
          {stickers.length > 0 && (
            <div
              className={`px-6 py-4 border-t ${theme.border} flex items-center justify-between flex-wrap gap-4`}
            >
              <div className={`text-sm ${theme.textSecondary}`}>
                Showing page {pagination.page || 1} of{" "}
                {pagination.totalPages || 1} ({pagination.total || 0} total)
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className={`p-2 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${theme.hover}`}
                >
                  <ChevronLeft size={18} />
                </button>
                <span className={`px-4 py-2 ${theme.textPrimary}`}>
                  {currentPage}
                </span>
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={!pagination.hasNextPage}
                  className={`p-2 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${theme.hover}`}
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ========== CREATE MODAL ========== */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in">
          <div
            className={`${theme.cardBg} rounded-2xl max-w-md w-full p-6 border ${theme.border} animate-in zoom-in-95`}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className={`text-2xl font-bold ${theme.textPrimary}`}>
                Create Sticker
              </h2>
              <button
                onClick={closeModals}
                className={`${theme.textSecondary} hover:${theme.textPrimary} transition-colors`}
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleCreateSticker} className="space-y-5">
              <div>
                <label
                  className={`block text-sm font-medium ${theme.textSecondary} mb-2`}
                >
                  Sticker Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="e.g., Happy Face"
                  maxLength={100}
                  className={`w-full px-4 py-2 rounded-lg border ${theme.inputBorder} ${theme.inputBg} ${theme.textPrimary} focus:outline-none focus:ring-2 focus:ring-orange-400 transition-all`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium ${theme.textSecondary} mb-2`}
                >
                  Image *
                </label>
                {imagePreview ? (
                  <div
                    className={`relative w-full p-4 rounded-lg border-2 border-dashed ${theme.inputBorder} text-center`}
                  >
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-32 h-32 mx-auto object-cover rounded-lg mb-3"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setImageFile(null);
                        setImagePreview(null);
                      }}
                      className="text-red-400 hover:text-red-300 text-sm font-medium transition-colors"
                    >
                      Remove Image
                    </button>
                  </div>
                ) : (
                  <label
                    className={`flex flex-col items-center justify-center w-full p-6 rounded-lg border-2 border-dashed ${theme.inputBorder} cursor-pointer ${theme.hover} transition-all`}
                  >
                    <Upload className={`w-8 h-8 mb-2 ${theme.textSecondary}`} />
                    <span
                      className={`text-sm font-medium ${theme.textSecondary}`}
                    >
                      Click to upload or drag & drop
                    </span>
                    <span className={`text-xs ${theme.textSecondary}`}>
                      PNG, JPG, GIF, WebP up to 5MB
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                    />
                  </label>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={closeModals}
                  disabled={formSubmitting}
                  className={`flex-1 px-4 py-2.5 rounded-lg font-medium border ${theme.border} ${theme.textPrimary} ${theme.hover} transition-colors disabled:opacity-50`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={
                    formSubmitting ||
                    uploading ||
                    !formData.name.trim() ||
                    !imageFile
                  }
                  className="flex-1 px-4 py-2.5 bg-orange-400 text-black font-semibold rounded-lg hover:bg-orange-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {formSubmitting || uploading ? (
                    <>
                      <RefreshCw size={16} className="animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Plus size={16} />
                      Create Sticker
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========== EDIT MODAL ========== */}
      {showEditModal && selectedSticker && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in">
          <div
            className={`${theme.cardBg} rounded-2xl max-w-md w-full p-6 border ${theme.border} animate-in zoom-in-95`}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className={`text-2xl font-bold ${theme.textPrimary}`}>
                Edit Sticker
              </h2>
              <button
                onClick={closeModals}
                className={`${theme.textSecondary} hover:${theme.textPrimary} transition-colors`}
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleUpdateSticker} className="space-y-5">
              <div>
                <label
                  className={`block text-sm font-medium ${theme.textSecondary} mb-2`}
                >
                  Sticker Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="e.g., Happy Face"
                  maxLength={100}
                  className={`w-full px-4 py-2 rounded-lg border ${theme.inputBorder} ${theme.inputBg} ${theme.textPrimary} focus:outline-none focus:ring-2 focus:ring-orange-400 transition-all`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium ${theme.textSecondary} mb-2`}
                >
                  Current Image
                </label>
                <img
                  src={selectedSticker.imageUrl}
                  alt={selectedSticker.name}
                  className="w-full h-40 object-cover rounded-lg"
                  onError={(e) => {
                    e.target.src =
                      "https://via.placeholder.com/160?text=No+Image";
                  }}
                />
                <p className={`text-xs ${theme.textSecondary} mt-2`}>
                  Note: Image URL cannot be changed. Delete and recreate to
                  change image.
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={closeModals}
                  disabled={formSubmitting}
                  className={`flex-1 px-4 py-2.5 rounded-lg font-medium border ${theme.border} ${theme.textPrimary} ${theme.hover} transition-colors disabled:opacity-50`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={formSubmitting || !formData.name.trim()}
                  className="flex-1 px-4 py-2.5 bg-orange-400 text-black font-semibold rounded-lg hover:bg-orange-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {formSubmitting ? (
                    <>
                      <RefreshCw size={16} className="animate-spin" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <Check size={16} />
                      Update Sticker
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========== VIEW MODAL ========== */}
      {showViewModal && selectedSticker && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in">
          <div
            className={`${theme.cardBg} rounded-2xl max-w-md w-full p-6 border ${theme.border} animate-in zoom-in-95`}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className={`text-2xl font-bold ${theme.textPrimary}`}>
                Sticker Details
              </h2>
              <button
                onClick={closeModals}
                className={`${theme.textSecondary} hover:${theme.textPrimary} transition-colors`}
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-5">
              <div>
                <p
                  className={`text-xs uppercase font-semibold ${theme.textSecondary} mb-2`}
                >
                  Image
                </p>
                <img
                  src={selectedSticker.imageUrl}
                  alt={selectedSticker.name}
                  className="w-full h-48 object-cover rounded-lg"
                  onError={(e) => {
                    e.target.src =
                      "https://via.placeholder.com/200?text=No+Image";
                  }}
                />
              </div>

              <div>
                <p
                  className={`text-xs uppercase font-semibold ${theme.textSecondary} mb-2`}
                >
                  Name
                </p>
                <p className={`text-lg font-semibold ${theme.textPrimary}`}>
                  {selectedSticker.name}
                </p>
              </div>

              <div>
                <p
                  className={`text-xs uppercase font-semibold ${theme.textSecondary} mb-2`}
                >
                  Status
                </p>
                <div className="flex gap-2 flex-wrap">
                  <span
                    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
                      selectedSticker.isActive
                        ? isDarkMode
                          ? "bg-green-500/20 text-green-400 border border-green-500/30"
                          : "bg-green-100 text-green-700 border border-green-300"
                        : isDarkMode
                        ? "bg-gray-500/20 text-gray-400 border border-gray-500/30"
                        : "bg-gray-100 text-gray-700 border border-gray-300"
                    }`}
                  >
                    {selectedSticker.isActive ? "Active" : "Inactive"}
                  </span>
                  {selectedSticker.isDeleted && (
                    <span
                      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
                        isDarkMode
                          ? "bg-red-500/20 text-red-400 border border-red-500/30"
                          : "bg-red-100 text-red-700 border border-red-300"
                      }`}
                    >
                      Deleted
                    </span>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p
                    className={`text-xs uppercase font-semibold ${theme.textSecondary} mb-2`}
                  >
                    Created
                  </p>
                  <p className={`text-sm ${theme.textPrimary}`}>
                    {new Date(selectedSticker.createdAt).toLocaleDateString()}
                  </p>
                </div>
                {selectedSticker.deletedAt && (
                  <div>
                    <p
                      className={`text-xs uppercase font-semibold ${theme.textSecondary} mb-2`}
                    >
                      Deleted
                    </p>
                    <p className={`text-sm ${theme.textPrimary}`}>
                      {new Date(selectedSticker.deletedAt).toLocaleDateString()}
                    </p>
                  </div>
                )}
              </div>

              <div>
                <p
                  className={`text-xs uppercase font-semibold ${theme.textSecondary} mb-2`}
                >
                  Image URL
                </p>
                <div
                  className={`p-3 rounded-lg break-all text-xs font-mono ${theme.inputBg} border ${theme.inputBorder}`}
                >
                  {selectedSticker.imageUrl}
                </div>
              </div>

              <div>
                <p
                  className={`text-xs uppercase font-semibold ${theme.textSecondary} mb-2`}
                >
                  Sticker ID
                </p>
                <div
                  className={`p-3 rounded-lg break-all text-xs font-mono ${theme.inputBg} border ${theme.inputBorder}`}
                >
                  {selectedSticker._id}
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={closeModals}
                  className="flex-1 px-4 py-2.5 bg-orange-400 text-black font-semibold rounded-lg hover:bg-orange-500 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* ========== CONFIRM DELETE MODAL ========== */}
      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={() => {
          setShowConfirmModal(false);
          setConfirmAction(null);
        }}
        onConfirm={() => confirmAction && confirmAction()}
        title="Delete Sticker"
        message="Are you sure you want to delete this sticker? It can be restored later."
        confirmText="Delete"
        isDarkMode={isDarkMode}
        isLoading={formSubmitting}
      />
    </div>
  );
};
export default StickersManagement;
